import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getProfile } from "@/services/profile.service";
import useProfileStore from "@/store/useProfileStore";
import { showToast, TOAST_TYPES } from "@/lib/toast-utils/toast.util";
import { getCookie } from "cookies-next";
import { COOKIE_CONFIG } from "@/config";

export const useProfileData = () => {
  const { setProfile, setLoading, setError } = useProfileStore();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [isClient, setIsClient] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["getProfile"],
    queryFn: getProfile,
    enabled: isClient && !!isLoggedIn && !!getCookie(COOKIE_CONFIG.loggedIn),
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      // Don't retry on 401 (unauthorized)
      if (error?.response?.status === 401) return false;
      return failureCount < 3;
    },
  });

  // Initialize client-side state
  useEffect(() => {
    setIsClient(true);
    setIsLoggedIn(!!getCookie(COOKIE_CONFIG.loggedIn));
  }, []);

  // Add a method to refresh auth state - can be called after auth operations
  const refreshAuthState = () => {
    const cookieValue = getCookie(COOKIE_CONFIG.loggedIn);
    setIsLoggedIn(!!cookieValue);
  };

  // Listen for custom auth state change events
  useEffect(() => {
    const handleAuthChange = () => refreshAuthState();
    
    window.addEventListener('auth-state-changed', handleAuthChange);
    return () => window.removeEventListener('auth-state-changed', handleAuthChange);
  }, []);

  // Handle success and error states with useEffect
  useEffect(() => {
    if (data) {
      setProfile(data);
      setLoading(false);
      setError(null);
    }
  }, [data, setProfile, setLoading, setError]);

  useEffect(() => {
    if (error) {
      const errorMessage = error?.message || "Failed to load profile data";
      setError(errorMessage);
      setLoading(false);
      showToast(TOAST_TYPES.error, errorMessage);
      console.error("Profile fetch error:", error);
    }
  }, [error, setError, setLoading]);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  return {
    profileData: data,
    isLoading: isClient ? isLoading : true, // Show loading during hydration
    error,
    refetch,
    refreshAuthState, // Expose method to refresh auth state
    isAuthenticated: isClient ? !!isLoggedIn : false, // Consistent server/client rendering
    userType: data?.userType, // Add userType for easy access
    isFirstLogin: data?.isFirstLogin, // Add isFirstLogin for easy access
  };
};
